import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, SendHorizontal } from "lucide-react";

const GREETING =
  "Hi, I'm the Heritage assistant. Ask me about our services, the areas we cover, or how quoting works. For anything specific, Lucas is one call away on 0456 492 849.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `s-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(part.slice(6));
            if (ev.type === "delta" || ev.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: (copy[copy.length - 1]?.text || "") + (ev.text || "") };
                return copy;
              });
            }
          } catch (e) { /* ignore malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "The assistant is unavailable right now. Please call Lucas on 0456 492 849 or send a quote enquiry." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Chat with us"}
        data-testid="chat-open-button"
        className="fixed bottom-5 left-5 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-charcoal text-gold shadow-xl shadow-charcoal/30 transition-colors hover:bg-charcoal-soft"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[84px] left-4 z-[70] flex h-[520px] max-h-[72vh] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-lg border border-charcoal/15 bg-ivory shadow-2xl shadow-charcoal/25 sm:left-5"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between border-b border-charcoal/10 bg-charcoal px-5 py-4">
              <div>
                <p className="font-serif text-lg leading-tight text-ivory">Heritage Assistant</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-light">Powered by ChatGPT</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" className="rounded-full p-1.5 text-ivory/60 hover:text-ivory">
                <X size={17} />
              </button>
            </div>

            <div ref={bodyRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid="chat-message">
                  <div
                    className={`max-w-[85%] rounded-xl px-4 py-2.5 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-charcoal text-ivory"
                        : "rounded-bl-sm border border-charcoal/10 bg-paper text-charcoal/85"
                    }`}
                  >
                    {m.text || <span className="inline-block h-3 w-1.5 animate-pulse rounded-sm bg-gold" />}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-charcoal/10 bg-paper p-3">
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about decks, verandahs, quoting…"
                  className="field !py-2.5 !text-[13px]"
                  data-testid="chat-input"
                  maxLength={1000}
                />
                <button
                  onClick={send}
                  disabled={streaming || !input.trim()}
                  aria-label="Send message"
                  data-testid="chat-send"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-charcoal transition-opacity hover:opacity-90 disabled:opacity-40"
                >
                  <SendHorizontal size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
